import { useEffect, useRef, useState } from "react";

/**
 * ScrollReveal — pans a large image into view as user scrolls.
 * Uses requestAnimationFrame + IntersectionObserver so it works in every browser.
 *
 * The image starts zoomed-in and offset (only the top portion visible)
 * and finishes at full-frame scale as the section reaches the middle of the viewport.
 */
export const ScrollReveal = ({ src, alt = "", className = "", objectPosition = "center bottom", mobileObjectPosition, objectFit = "cover", overlay = null, children }) => {
  const wrapperRef = useRef(null);
  const imgRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const updateIsMobile = () => setIsMobile(mq.matches);
    updateIsMobile();
    mq.addEventListener("change", updateIsMobile);
    return () => mq.removeEventListener("change", updateIsMobile);
  }, []);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const img = imgRef.current;
    if (!wrapper || !img) return;

    // Only animate when in view
    const io = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0, rootMargin: "0px 0px 0px 0px" }
    );
    io.observe(wrapper);

    let rafId = null;
    const update = () => {
      rafId = null;
      const rect = wrapper.getBoundingClientRect();
      const vh = window.innerHeight;
      // progress = how far the section has travelled through the viewport
      // 0 when the top of the section is at the bottom of the viewport
      // 1 when the bottom of the section is at the top of the viewport
      const raw = (vh - rect.top) / (vh + rect.height);
      const progress = Math.max(0, Math.min(1, raw));
      // Ease the animation range so most of the reveal happens while the section is in the middle of the viewport
      const t = Math.max(0, Math.min(1, (progress - 0.1) / 0.55));

      // Start: zoomed-in, translated down (showing only top portion of image)
      // End: full scale, no offset — image fully revealed at natural size and holds
      const scale = 1.22 - 0.22 * t;   // 1.22 → 1.00
      const translateY = 14 - 14 * t;  // +14% → 0%

      img.style.transform = `translate3d(0, ${translateY}%, 0) scale(${scale})`;
    };

    const onScroll = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(update);
    };

    update();
    if (visible) window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [visible]);

  return (
    <section ref={wrapperRef} data-testid="scroll-reveal" className={`relative overflow-hidden ${className}`}>
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className="absolute inset-0 w-full h-full will-change-transform"
        style={{ objectFit, objectPosition: isMobile && mobileObjectPosition ? mobileObjectPosition : objectPosition, transformOrigin: "center center" }}
      />
      {overlay}
      {children}
    </section>
  );
};
